import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Switch } from "../../../components/ui";
import { readImageFile } from "../../../utils/files";
import "./genre-form.css";

/** Content types a genre can be attached to (API `type` / `genre_type`). */
export const GENRE_TYPES = [
  { type: "movies", label: "Videos", icon: "ri-film-line" },
  { type: "podcast", label: "Podcast", icon: "ri-mic-line" },
  { type: "tv shows", label: "TV Shows", icon: "ri-tv-2-line" },
  { type: "live channels", label: "Live Channels", icon: "ri-broadcast-line" },
];

/** One form row per content type, filled from the existing genres if any. */
export const initialRows = (genres = []) =>
  GENRE_TYPES.map(({ type }) => {
    const genre = genres.find((g) => g.genre_type === type);
    return {
      type,
      _id: genre?._id || "",
      is_enabled: genre ? Boolean(genre.is_enabled) : false,
      url: genre?.genre_image?.url || "",
      imageBase64: "",
      isImageSelected: false,
      fileName: "",
    };
  });

/**
 * Genre name + one switch and image per content type.
 * `onSubmit(name, rows)` performs the API calls and resolves when done.
 */
const GenreForm = ({ initialName = "", rows: initial, onSubmit, submitLabel }) => {
  const { t } = useTranslation();
  const [name, setName] = useState(initialName);
  const [rows, setRows] = useState(initial);
  const [saving, setSaving] = useState(false);
  const isEdit = initial.some((row) => row._id);

  const updateRow = (type, changes) =>
    setRows((current) =>
      current.map((row) => (row.type === type ? { ...row, ...changes } : row))
    );

  const pickImage = (type, file) => {
    if (!file) return;
    const url = readImageFile(file, (imageBase64) =>
      updateRow(type, { imageBase64 })
    );
    updateRow(type, { url, isImageSelected: true, fileName: file.name });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const enabled = rows.filter((row) => row.is_enabled);
    if (!name.trim()) {
      toast.error(t("Please enter a genre name"));
      return;
    }
    if (enabled.length === 0 && !rows.some((row) => row._id)) {
      toast.error(t("Please add a category to genre!"));
      return;
    }
    if (enabled.some((row) => !row.url || (row.isImageSelected && !row.imageBase64))) {
      toast.error(t("Please upload image for the active category"));
      return;
    }
    setSaving(true);
    try {
      await onSubmit(name.trim(), rows);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="ui-card genre-form" onSubmit={handleSubmit}>
      <div className="genre-form-body">
        <div className="ui-field">
          <label htmlFor="genre-name">{t("Genre Name")}</label>
          <input
            id="genre-name"
            className="ui-input"
            value={name}
            placeholder={t("Genre Name")}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="ui-field">
          <span className="ui-label">{t("Categories")}</span>
          <ul className="genre-rows">
            {GENRE_TYPES.map(({ type, label, icon }) => {
              const row = rows.find((r) => r.type === type);
              const inputId = `genre-image-${type.replace(" ", "-")}`;
              return (
                <li key={type} className="genre-row" data-enabled={row.is_enabled || undefined}>
                  <span className="genre-row-icon" aria-hidden="true">
                    <i className={icon}></i>
                  </span>
                  <div className="genre-row-body">
                    <strong>{t(label)}</strong>
                    {isEdit && (

                      <span className="ui-hint">

                        {row._id ? t("Existing category") : t("New category")}

                      </span>

                    )}
                  </div>
                  <div className="genre-row-image">
                    {row.url ? (
                      <img src={row.url} alt="" />
                    ) : (
                      <span className="genre-row-placeholder" aria-hidden="true">
                        <i className="ri-image-line"></i>
                      </span>
                    )}
                    <label htmlFor={inputId} className="genre-row-upload">
                      <i className="ri-upload-2-line" aria-hidden="true"></i>
                      {row.url ? t("Change image") : t("Choose an image")}
                    </label>
                    <input
                      id={inputId}
                      type="file"
                      accept=".png, .jpg, .jpeg"
                      className="sr-only"
                      onChange={(e) => {
                        pickImage(type, e.target.files && e.target.files[0]);
                        e.target.value = "";
                      }}
                    />
                  </div>
                  <Switch
                    checked={row.is_enabled}
                    onChange={(checked) => updateRow(type, { is_enabled: checked })}
                    label={t(label)}
                    onLabel={t("Active")}
                    offLabel={t("Inactive")}
                  />
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <div className="genre-form-footer">
        <Link to="/dashboard/category-list" className="ui-btn ui-btn-secondary">
          {t("cancel")}
        </Link>
        <button type="submit" className="ui-btn" disabled={saving}>
          {saving && <span className="genre-spinner" aria-hidden="true" />}
          {submitLabel}
        </button>
      </div>
    </form>
  );
};

export default GenreForm;
